import React, { useEffect, useMemo, useState } from "react";
import {
  Grid,
  Card,
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Switch,
  Chip,
  InputAdornment,
  IconButton,
  Tooltip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
} from "@mui/material";
import { useFormik } from "formik";
import dayjs from "dayjs";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import SendIcon from "@mui/icons-material/Send";
import LockResetIcon from "@mui/icons-material/LockReset";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DoDisturbOnOutlinedIcon from "@mui/icons-material/DoDisturbOnOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { alpha } from "@mui/material/styles";
import PortalAccessCard from "../../../Components/ParentPortal/PortalAccessCard";
import { useNavigate } from "react-router-dom";
import { useStyles } from "../../../theme/appStyles";
import { useNotifier } from "../../../Core/Notifier";
import { useConfirm, confirmPresets } from "../../../Components/Confirm";
import {
  useCreateParentMutation,
  useUpdateParentMutation,
  useGenerateParentCredentialsMutation,
  useEnableParentPortalMutation,
  useResetParentPasswordMutation,
  useSendParentLoginDetailsMutation,
  useSendParentResetLinkMutation,
  useToggleParentAccessMutation,
} from "../../../Api/attoDeskApi";
import {
  validateUserEmail,
  formatPhoneNumber,
  normalizePhoneNumber,
  isValidPhoneNumber,
} from "../../../Components/Validation/validationUtils";

export interface IParentFormValues {
  first_name: string;
  middle_name: string;
  last_name: string;
  contact_number: string;
  email: string;
  address: string;
  // ISO country code — "" | "CA" | "US". Authoritative source for
  // Worldline merchant routing (backend enforces the rest).
  country_code: "" | "CA" | "US";
  is_email_notification: boolean;
  is_sms_notification: boolean;
  is_payment_reminder: boolean;
}

export interface IParentAccount {
  has_account: boolean;
  username?: string | null;
  account_email?: string | null;
  account_status?: string | null; // 'active' | 'inactive'
  last_login?: string | null;
}

const emptyValues: IParentFormValues = {
  first_name: "",
  middle_name: "",
  last_name: "",
  contact_number: "",
  email: "",
  address: "",
  country_code: "",
  is_email_notification: false,
  is_sms_notification: false,
  is_payment_reminder: false,
};

interface Props {
  parentId?: string;
  initialValues?: Partial<IParentFormValues>;
  title?: string;
  account?: IParentAccount;
}

const ParentForm = ({ parentId, initialValues, title, account }: Props) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { showMessage, showErrorMessage } = useNotifier();
  const confirm = useConfirm();

  const [createParent, { isLoading: creating }] = useCreateParentMutation();
  const [updateParent, { isLoading: updating }] = useUpdateParentMutation();
  const [genCreds] = useGenerateParentCredentialsMutation();
  const [enablePortal, { isLoading: enabling }] = useEnableParentPortalMutation();
  const [resetPassword, { isLoading: resetting }] = useResetParentPasswordMutation();
  const [sendLogin, { isLoading: sendingLogin }] = useSendParentLoginDetailsMutation();
  const [sendResetLink, { isLoading: sendingReset }] = useSendParentResetLinkMutation();
  const [toggleAccess, { isLoading: toggling }] = useToggleParentAccessMutation();
  const isLoading = creating || updating;

  const hasAccount = !!account?.has_account;
  const isEnabled = (account?.account_status ?? "active") === "active";

  // One-time credentials dialog (shown after create / enable / reset).
  const [creds, setCreds] = useState<{ username: string; password: string } | null>(null);

  // Reset Password dialog state. Kept co-located here so the disable /
  // enable toggle in the same card doesn't need a global bus.
  const [resetOpen, setResetOpen] = useState(false);
  const [resetMode, setResetMode] = useState<"generate" | "manual">("generate");
  const [resetPw, setResetPw] = useState("");
  const [resetPwConfirm, setResetPwConfirm] = useState("");
  // Auto-generated preview shown on the Create page before saving.
  const [preview, setPreview] = useState<{ username: string; password: string }>({ username: "", password: "" });

  const loginUrl = `${window.location.origin}/login`;

  const formik = useFormik<IParentFormValues>({
    initialValues: { ...emptyValues, ...initialValues },
    enableReinitialize: true,
    validate: (values) => {
      const errors: Record<string, string> = {};
      if (!values.first_name.trim()) errors.first_name = "First name is required";
      const emailErr = validateUserEmail(values.email);
      if (emailErr) errors.email = emailErr;
      // Country is required on create; on edit we tolerate a legacy
      // blank value but if the admin picks something it must be CA/US.
      if (!parentId && !values.country_code) {
        errors.country_code = "Country is required";
      } else if (values.country_code && !["CA", "US"].includes(values.country_code)) {
        errors.country_code = "Country must be Canada or United States";
      }
      // Phone: required on create, and whenever present must be exactly 10 digits.
      const phoneDigits = normalizePhoneNumber(values.contact_number);
      if ((!parentId && phoneDigits.length === 0) || (phoneDigits.length > 0 && !isValidPhoneNumber(phoneDigits))) {
        errors.contact_number = "Enter a valid 10-digit contact number.";
      }
      return errors;
    },
    onSubmit: async (values) => {
      try {
        // On create, provision the portal login automatically using the
        // previewed credentials so what the admin saw matches what is saved.
        // Always send raw digits — the backend stores the normalized phone.
        const normalizedValues = { ...values, contact_number: normalizePhoneNumber(values.contact_number) };
        const body = parentId
          ? normalizedValues
          : { ...normalizedValues, portal_access: true, username: preview.username, password: preview.password };
        const res = parentId
          ? await updateParent({ id: parentId, body }).unwrap()
          : await createParent(body).unwrap();
        if (!res.status) {
          showErrorMessage(res.message);
          return;
        }
        showMessage(res.message);
        const portal = (res as any).portal;
        if (portal?.temp_password) {
          setCreds({ username: portal.username, password: portal.temp_password });
        } else {
          // Edit → return to the same parent's profile. Create → list.
          navigate(parentId ? `/parents/view/${parentId}` : "/parents/list");
        }
      } catch {
        showErrorMessage("Something went wrong");
      }
    },
  });

  // Fetch a preview username + temp password for the Create page.
  useEffect(() => {
    if (parentId) return;
    (async () => {
      try {
        const res = await genCreds().unwrap();
        const data = (res as any).data || {};
        if (data.username) setPreview({ username: data.username, password: data.password || "" });
      } catch {
        /* non-fatal — server will still generate on save */
      }
    })();
  }, [parentId, genCreds]);

  const field = (name: keyof IParentFormValues, label: string, required = false) => (
    <Grid item lg={6} md={6} sm={12} xs={12}>
      <Typography variant="subtitle1">
        {label} {required && <span className={classes.requiedfield}>*</span>}
      </Typography>
      <TextField
        fullWidth
        size="small"
        placeholder={label}
        {...formik.getFieldProps(name)}
        className={classes.textField}
        error={formik.touched[name] && Boolean(formik.errors[name])}
        helperText={formik.touched[name] && (formik.errors[name] as string)}
      />
    </Grid>
  );

  // Read-only display field (username / temp password preview).
  const readonlyField = (label: string, value: string, mono = false) => (
    <Grid item lg={6} md={6} sm={12} xs={12}>
      <Typography variant="subtitle1">{label}</Typography>
      <TextField
        fullWidth
        size="small"
        value={value || "—"}
        className={classes.textField}
        InputProps={{
          readOnly: true,
          sx: mono ? { fontFamily: "monospace", letterSpacing: 0.5 } : undefined,
          endAdornment: value ? (
            <InputAdornment position="end">
              <Tooltip title={`Copy ${label.toLowerCase()}`}>
                <IconButton size="small" onClick={() => copyText(value, label)}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ) : undefined,
        }}
      />
    </Grid>
  );

  const canSubmit = useMemo(
    () => formik.values.first_name.trim() !== "" && formik.values.email.trim() !== "",
    [formik.values.first_name, formik.values.email]
  );

  const copyText = (text: string, label = "Value") => {
    navigator.clipboard
      ?.writeText(text)
      .then(() => showMessage(`${label} copied`))
      .catch(() => {});
  };

  const copyCreds = () => {
    if (!creds) return;
    copyText(`Username: ${creds.username}\nPassword: ${creds.password}\nLogin: ${loginUrl}`, "Credentials");
  };

  // ── Portal account actions (edit page) ──
  const runAction = async (
    fn: () => Promise<any>,
    opts: { showCreds?: boolean } = {}
  ) => {
    try {
      const res = await fn();
      if (!res?.status) {
        showErrorMessage(res?.message || "Action failed");
        return;
      }
      showMessage(res.message);
      const portal = res.portal;
      if (opts.showCreds && portal?.temp_password) {
        setCreds({ username: portal.username, password: portal.temp_password });
      }
    } catch {
      showErrorMessage("Something went wrong");
    }
  };

  // Enabling access is non-destructive → runs immediately.
  const handleEnable = () => runAction(() => enablePortal(parentId!).unwrap(), { showCreds: true });

  // Opens the redesigned Reset Password dialog with a fresh clean state.
  const openResetDialog = () => {
    setResetMode("generate");
    setResetPw("");
    setResetPwConfirm("");
    setResetOpen(true);
  };
  const closeResetDialog = () => {
    setResetOpen(false);
    setResetPw("");
    setResetPwConfirm("");
  };

  // Manual-mode client-side validation. Server re-validates (min 8 + match)
  // — this is just a UX gate so the Send button reflects readiness.
  const manualValid =
    resetPw.trim().length >= 8 && resetPw === resetPwConfirm;
  const canSubmitReset = resetMode === "generate" ? !resetting : manualValid && !resetting;

  const submitReset = async () => {
    try {
      const args = resetMode === "generate"
        ? parentId!
        : { id: parentId!, mode: "manual" as const, new_password: resetPw, confirm_password: resetPwConfirm };
      const res: any = await resetPassword(args as any).unwrap();
      if (!res.status) {
        showErrorMessage(res.message || "Reset failed.");
        return;
      }
      showMessage(res.message || "Password reset.");
      closeResetDialog();
      if (res.portal?.temp_password) {
        // Reuse the existing one-time credentials modal — the plaintext
        // is only readable here and cannot be retrieved again.
        setCreds({ username: res.portal.username, password: res.portal.temp_password });
      }
    } catch {
      showErrorMessage("Something went wrong");
    }
  };

  const handleSendLogin = () =>
    confirm({
      variant: "warning",
      title: "Send Login Details",
      message:
        "This emails the parent their username, a new temporary password and a set-password link.\nTheir current password will be replaced. Continue?",
      confirmText: "Send",
      successMessage: "Login details sent.",
      onConfirm: async () => {
        const res: any = await sendLogin(parentId!).unwrap();
        if (!res.status) throw new Error(res.message);
      },
    });

  const handleSendReset = () =>
    confirm({
      ...confirmPresets.resetPassword("Email the parent a self-service password reset link?"),
      confirmText: "Send Reset Link",
      successMessage: "Password reset link sent.",
      onConfirm: async () => {
        const res: any = await sendResetLink(parentId!).unwrap();
        if (!res.status) throw new Error(res.message);
      },
    });

  const handleToggle = (checked: boolean) => {
    // Disabling blocks login → confirm. Enabling is safe → run immediately.
    if (!checked) {
      confirm({
        ...confirmPresets.disableAccount(),
        successMessage: "Parent access disabled.",
        onConfirm: async () => {
          const res: any = await toggleAccess({ parent_id: parentId, status: "disabled" }).unwrap();
          if (!res.status) throw new Error(res.message);
        },
      });
      return;
    }
    runAction(() => toggleAccess({ parent_id: parentId, status: "active" }).unwrap());
  };

  const anyActionRunning = enabling || resetting || sendingLogin || sendingReset || toggling;

  // ── Redesigned Portal Access section (Aug 2026) ──
  // Presents the credential card the spec describes: username, login
  // email, masked password, status chip, last-login line, and three
  // clear-labelled action buttons. Never renders the parent's real
  // password (it isn't in the DTO in the first place — passwords are
  // in User::$hidden). Reset Password opens the two-mode dialog rather
  // than immediately mutating.
  const renderCredentialRow = (
    icon: React.ReactNode,
    label: string,
    value: React.ReactNode,
    mono = false,
    copyValue?: string,
  ) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1.25, borderBottom: "1px solid rgba(12,90,70,0.08)", "&:last-child": { borderBottom: "none" } }}>
      <Box sx={{ width: 32, height: 32, borderRadius: "10px", bgcolor: alpha("#0C5A46", 0.09), color: "#0C5A46", display: "flex", alignItems: "center", justifyContent: "center", "& svg": { fontSize: 18 }, flexShrink: 0 }}>
        {icon}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.08em" }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: "text.primary", fontFamily: mono ? "monospace" : undefined, letterSpacing: mono ? 2 : undefined, mt: 0.25, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {value}
        </Typography>
      </Box>
      {copyValue && (
        <Tooltip title={`Copy ${label.toLowerCase()}`}>
          <IconButton size="small" onClick={() => copyText(copyValue, label)}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );

  const renderPortalSection = () => {
    // Edit page → hand the whole surface to the shared
    // PortalAccessCard so this page and the Communication → Parent
    // Portal Management drawer can never drift. The shared card owns
    // its own Reset Password dialog + one-time credentials modal;
    // the local `creds` state below only fires after createParent()
    // on the create-page path.
    if (parentId) {
      return (
        <Box sx={{ p: 3 }}>
          <PortalAccessCard
            parentId={parentId}
            parentName={[formik.values.first_name, formik.values.last_name].filter(Boolean).join(" ")}
            contactEmail={formik.values.email}
            account={{
              has_account: hasAccount,
              username: account?.username,
              account_email: account?.account_email,
              account_status: account?.account_status,
              last_login: account?.last_login,
            }}
          />
        </Box>
      );
    }

    // Legacy hasAccount branch preserved below as fallback for the
    // (unusual) case where the form is rendered without a parentId
    // but still has account data. Kept for defensive rendering only.
    if (hasAccount) {
      // Warn if the parent contact email and the portal login email differ.
      const contactEmail = (formik.values.email || "").trim().toLowerCase();
      const portalEmail = (account?.account_email || "").trim().toLowerCase();
      const emailsDiffer = !!portalEmail && !!contactEmail && contactEmail !== portalEmail;

      return (
        <Box sx={{ p: 3 }}>
          {/* Header row: title + status pill */}
          <Box sx={{
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5,
            mb: 2, flexWrap: "wrap",
          }}>
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.1em" }}>
                Portal Access
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.5 }}>
                <Box sx={{
                  width: 8, height: 8, borderRadius: "50%",
                  bgcolor: isEnabled ? "#16a34a" : "#9ca3af",
                }} />
                <Typography sx={{ fontSize: 14, fontWeight: 800, color: isEnabled ? "#0C5A46" : "text.secondary" }}>
                  {isEnabled ? "Active" : "Disabled"}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 11.5, color: "text.secondary", mt: 0.5 }}>
                {account?.last_login
                  ? `Last login: ${dayjs(account.last_login).format("MMM D, YYYY · h:mm A")}`
                  : "Never logged in"}
              </Typography>
            </Box>
          </Box>

          {/* Mint credential card */}
          <Box sx={{
            border: "1px solid rgba(12,90,70,0.18)",
            borderRadius: "14px",
            background: `linear-gradient(180deg, #EFF8F5 0%, #FFFFFF 100%)`,
            px: 2.25, py: 0.5,
            mb: 2,
          }}>
            {renderCredentialRow(<PersonOutlineIcon />, "Login Username", account?.username || "—", false, account?.username || undefined)}
            {renderCredentialRow(<EmailOutlinedIcon />, "Login Email", account?.account_email || "—", false, account?.account_email || undefined)}
            {renderCredentialRow(<KeyOutlinedIcon />, "Password", "••••••••", true)}
          </Box>

          {emailsDiffer && (
            <Box sx={{
              display: "flex", gap: 1, alignItems: "flex-start",
              p: 1.5, mb: 2, borderRadius: "10px",
              bgcolor: "#FFF8E8", border: "1px solid rgba(180,83,9,0.2)",
            }}>
              <Typography sx={{ fontSize: 12, color: "#B45309" }}>
                <b>Heads up —</b> the parent's contact email <code>{contactEmail}</code> differs from their portal login email <code>{portalEmail}</code>. Reset instructions are delivered to the portal login email.
              </Typography>
            </Box>
          )}

          {/* Action buttons */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={sendingLogin ? <CircularProgress size={14} color="inherit" /> : <SendIcon />}
              onClick={handleSendLogin}
              disabled={anyActionRunning}
              sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px", bgcolor: "#0C5A46", boxShadow: "none", "&:hover": { bgcolor: "#0a4939", boxShadow: "none" } }}
            >
              Send Login Instructions
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={resetting ? <CircularProgress size={14} /> : <LockResetIcon />}
              onClick={openResetDialog}
              disabled={anyActionRunning}
              sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px", borderColor: "rgba(12,90,70,0.35)", color: "#0C5A46", "&:hover": { borderColor: "#0C5A46", bgcolor: alpha("#0C5A46", 0.04) } }}
            >
              Reset Password
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={toggling ? <CircularProgress size={14} /> : (isEnabled ? <DoDisturbOnOutlinedIcon /> : <CheckCircleOutlineIcon />)}
              onClick={() => handleToggle(!isEnabled)}
              disabled={anyActionRunning}
              sx={{
                textTransform: "none", fontWeight: 700, borderRadius: "10px",
                borderColor: isEnabled ? "rgba(185,28,28,0.35)" : "rgba(22,163,74,0.35)",
                color: isEnabled ? "#B91C1C" : "#16a34a",
                "&:hover": { bgcolor: alpha(isEnabled ? "#B91C1C" : "#16a34a", 0.04), borderColor: isEnabled ? "#B91C1C" : "#16a34a" },
              }}
            >
              {isEnabled ? "Disable Access" : "Enable Access"}
            </Button>
          </Box>

          <Typography sx={{ fontSize: 11.5, color: "text.secondary", mt: 2 }}>
            <b>Send Login Instructions</b> emails the parent their username, a fresh temporary password, and a set-password link.
            {" "}<b>Reset Password</b> issues a new temporary password for you to share manually — the current password is never shown.
          </Typography>
        </Box>
      );
    }

    // No account yet, but parent exists (edit) → Not Created state.
    if (parentId) {
      return (
        <Box sx={{ p: 3 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.1em" }}>
            Portal Access
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.5, mb: 2 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#9ca3af" }} />
            <Typography sx={{ fontSize: 14, fontWeight: 800, color: "text.secondary" }}>Not Created</Typography>
          </Box>
          <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 1.5 }}>
            This parent doesn't have a portal login yet. Enable access to auto-generate a username and a temporary password.
          </Typography>
          <Button
            variant="contained"
            startIcon={enabling ? <CircularProgress size={16} color="inherit" /> : <LockOpenIcon />}
            onClick={handleEnable}
            disabled={enabling}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px", bgcolor: "#0C5A46", boxShadow: "none", "&:hover": { bgcolor: "#0a4939", boxShadow: "none" } }}
          >
            Create Portal Account
          </Button>
        </Box>
      );
    }

    // Create page → preview of the auto-generated credentials (unchanged).
    return (
      <>
        <Grid container spacing={3} px={"24px"} pt={"24px"}>
          {readonlyField("Username (auto-generated)", preview.username, true)}
          {readonlyField("Temporary Password (auto-generated)", preview.password, true)}
        </Grid>
        <Box px={"24px"} pb={"24px"} pt={1.5}>
          <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
            A portal login is created automatically when you save this parent. The temporary password is shown once —
            the parent is prompted to change it on first login.
          </Typography>
        </Box>
      </>
    );
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <Grid container spacing={3}>
        <Grid item lg={12} md={12} sm={12} xs={12}>
          <Card className={classes.card}>
            <Box sx={{ borderBottom: 2, p: 2 }} className={classes.titleGrid}>
              <Typography className={classes.cardTitle}>
                {title || (parentId ? "Edit Parent" : "Add Parent")}
              </Typography>
            </Box>
            <Grid container spacing={3} p={"24px"}>
              {field("first_name", "First Name", true)}
              {field("last_name", "Last Name")}
              {field("middle_name", "Middle Name")}
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <Typography variant="subtitle1">
                  Phone {!parentId && <span className={classes.requiedfield}>*</span>}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  type="tel"
                  placeholder="647-567-5666"
                  inputProps={{ inputMode: "numeric" }}
                  // Formatted for display; raw digits kept in state.
                  value={formatPhoneNumber(formik.values.contact_number)}
                  onChange={(e) =>
                    formik.setFieldValue("contact_number", normalizePhoneNumber(e.target.value))
                  }
                  onBlur={formik.handleBlur}
                  name="contact_number"
                  className={classes.textField}
                  error={formik.touched.contact_number && Boolean(formik.errors.contact_number)}
                  helperText={formik.touched.contact_number && (formik.errors.contact_number as string)}
                />
              </Grid>
              {field("email", "Email Address", true)}
              {field("address", "Address")}
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <Typography variant="subtitle1">
                  Country {!parentId && <span className={classes.requiedfield}>*</span>}
                </Typography>
                <TextField
                  select
                  fullWidth
                  size="small"
                  name="country_code"
                  value={formik.values.country_code}
                  onChange={(e) => formik.setFieldValue("country_code", e.target.value)}
                  onBlur={formik.handleBlur}
                  className={classes.textField}
                  error={formik.touched.country_code && Boolean(formik.errors.country_code)}
                  helperText={
                    (formik.touched.country_code && (formik.errors.country_code as string)) ||
                    (parentId && !formik.values.country_code
                      ? "Not set — required before a payment card can be added."
                      : "Determines which Worldline merchant account processes this parent's cards.")
                  }
                  SelectProps={{ displayEmpty: true }}
                >
                  <MenuItem value="">
                    <em>Select country…</em>
                  </MenuItem>
                  <MenuItem value="CA">Canada</MenuItem>
                  <MenuItem value="US">United States</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Card>
        </Grid>

        {/* ── Portal Access (always visible) ──
            The card's inner "Portal Access · Active" header lives
            inside renderPortalSection so the mint credential block
            can sit flush against it. No duplicate title bar. */}
        <Grid item lg={12} md={12} sm={12} xs={12}>
          <Card className={classes.card} sx={{ overflow: "hidden" }}>
            {renderPortalSection()}
          </Card>
        </Grid>

        <Grid item lg={12} md={12} sm={12} xs={12}>
          <Card className={classes.card}>
            <Box sx={{ borderBottom: 2, p: 2 }} className={classes.titleGrid}>
              <Typography className={classes.cardTitle}>Communication Preferences</Typography>
            </Box>
            <Grid container spacing={1} p={"24px"}>
              <Grid item lg={4} md={6} sm={12} xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formik.values.is_email_notification}
                      onChange={(e) => formik.setFieldValue("is_email_notification", e.target.checked)}
                    />
                  }
                  label="Email reminders"
                />
              </Grid>
              <Grid item lg={4} md={6} sm={12} xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formik.values.is_sms_notification}
                      onChange={(e) => formik.setFieldValue("is_sms_notification", e.target.checked)}
                    />
                  }
                  label="SMS reminders"
                />
              </Grid>
              <Grid item lg={4} md={6} sm={12} xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formik.values.is_payment_reminder}
                      onChange={(e) => formik.setFieldValue("is_payment_reminder", e.target.checked)}
                    />
                  }
                  label="Payment reminders"
                />
              </Grid>
            </Grid>
          </Card>
        </Grid>

        <Grid item lg={12} md={12} sm={12} xs={12}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<CloseIcon />}
              className={classes.addButton}
              onClick={() => navigate(parentId ? `/parents/view/${parentId}` : "/parents/list")}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={isLoading ? <CircularProgress size={18} /> : <SaveIcon />}
              className={classes.addButton}
              onClick={() => formik.handleSubmit()}
              disabled={!canSubmit || isLoading}
            >
              {parentId ? "Update" : "Save"}
            </Button>
          </Box>
        </Grid>
      </Grid>

      {/* ══════════ One-time credentials modal ══════════
          Shown after a successful create / enable / reset. This is the
          ONLY place the plaintext temporary password is visible. It
          cannot be re-opened later — closing the dialog discards the
          value from React state, and there is no server endpoint that
          returns a stored plaintext password. */}
      <Dialog open={!!creds} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "16px" } }}>
        <DialogTitle sx={{ fontWeight: 700, pb: 0 }}>
          Password Reset Successful
        </DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 2 }}>
            Share this temporary password securely with the parent. It will not be shown again after this window is closed.
          </Typography>
          <Box sx={{
            border: "1px solid rgba(12,90,70,0.18)", borderRadius: "12px",
            background: `linear-gradient(180deg, #EFF8F5 0%, #FFFFFF 100%)`,
            px: 2, py: 0.5, mb: 2,
          }}>
            {renderCredentialRow(<PersonOutlineIcon />, "Username", creds?.username || "—", false, creds?.username || undefined)}
            {renderCredentialRow(<KeyOutlinedIcon />, "Temporary Password", creds?.password || "—", true, creds?.password || undefined)}
          </Box>
          <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
            Parent will be required to change the password after login.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, flexWrap: "wrap", gap: 0.75 }}>
          <Button size="small" startIcon={<ContentCopyIcon />} onClick={copyCreds} sx={{ textTransform: "none" }}>
            Copy Login Details
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              const wasCreate = !parentId;
              setCreds(null);
              if (wasCreate) navigate("/parents/list");
            }}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px", ml: "auto", bgcolor: "#0C5A46", boxShadow: "none", "&:hover": { bgcolor: "#0a4939", boxShadow: "none" } }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password dialog was moved into the shared
          PortalAccessCard so the Student Profile → Parent / Guardian
          surface and the Parent Portal Management drawer can never
          drift. The dialog below is retained only as a defensive
          fallback for a very unusual code path (renderPortalSection's
          legacy hasAccount branch) and is inert on the create page. */}
      <Dialog open={resetOpen} onClose={closeResetDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "16px" } }}>
        <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>
          Reset Parent Portal Password
        </DialogTitle>
        <DialogContent dividers>
          {/* Identity block (read-only) */}
          <Box sx={{
            p: 1.75, mb: 2.5, borderRadius: "12px",
            border: "1px solid rgba(12,90,70,0.18)",
            background: `linear-gradient(180deg, #EFF8F5 0%, #FFFFFF 100%)`,
          }}>
            <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.08em" }}>
              Parent
            </Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 700, mt: 0.25 }}>
              {[formik.values.first_name, formik.values.last_name].filter(Boolean).join(" ") || "This parent"}
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mt: 1 }}>
              <Box>
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Username
                </Typography>
                <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>{account?.username || "—"}</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Login Email
                </Typography>
                <Typography sx={{ fontSize: 12.5, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{account?.account_email || "—"}</Typography>
              </Box>
            </Box>
          </Box>

          <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary", mb: 1, textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Choose how to reset
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
            {[
              { key: "generate", icon: <AutoAwesomeOutlinedIcon />, title: "Generate Temporary Password", desc: "Recommended. System generates a secure temporary password to share with the parent." },
              { key: "manual",   icon: <EditOutlinedIcon />,        title: "Set Password Manually",        desc: "Type a temporary password yourself. Minimum 8 characters." },
            ].map((opt) => {
              const active = resetMode === (opt.key as any);
              return (
                <Box
                  key={opt.key}
                  onClick={() => setResetMode(opt.key as any)}
                  sx={{
                    px: 1.75, py: 1.25, borderRadius: "12px", cursor: "pointer",
                    display: "flex", gap: 1.25, alignItems: "flex-start",
                    border: `1.5px solid ${active ? "#0C5A46" : "rgba(0,0,0,0.1)"}`,
                    bgcolor: active ? alpha("#0C5A46", 0.05) : "transparent",
                    transition: "all 150ms",
                  }}
                >
                  <Box sx={{
                    width: 20, height: 20, borderRadius: "50%",
                    border: `2px solid ${active ? "#0C5A46" : "rgba(0,0,0,0.25)"}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0, mt: 0.25,
                  }}>
                    {active && <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "#0C5A46" }} />}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <Box sx={{ color: active ? "#0C5A46" : "text.secondary", "& svg": { fontSize: 17 } }}>
                        {opt.icon}
                      </Box>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: active ? "#0C5A46" : "text.primary" }}>{opt.title}</Typography>
                    </Box>
                    <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.25 }}>{opt.desc}</Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>

          {resetMode === "manual" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Box>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary", mb: 0.5 }}>New Temporary Password *</Typography>
                <TextField
                  fullWidth size="small" type="text" autoComplete="new-password"
                  value={resetPw}
                  onChange={(e) => setResetPw(e.target.value)}
                  placeholder="Minimum 8 characters"
                  inputProps={{ maxLength: 72 }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px", fontFamily: "monospace", letterSpacing: 1 } }}
                />
              </Box>
              <Box>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary", mb: 0.5 }}>Confirm Password *</Typography>
                <TextField
                  fullWidth size="small" type="text" autoComplete="new-password"
                  value={resetPwConfirm}
                  onChange={(e) => setResetPwConfirm(e.target.value)}
                  placeholder="Re-enter the password"
                  inputProps={{ maxLength: 72 }}
                  error={resetPwConfirm.length > 0 && resetPw !== resetPwConfirm}
                  helperText={resetPwConfirm.length > 0 && resetPw !== resetPwConfirm ? "Passwords do not match." : " "}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px", fontFamily: "monospace", letterSpacing: 1 } }}
                />
              </Box>
            </Box>
          )}

          <Typography sx={{ fontSize: 11.5, color: "text.secondary", mt: 0.5 }}>
            The current password is never displayed. Only the newly set temporary password is shown once, on the next screen.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeResetDialog} sx={{ textTransform: "none", borderRadius: "10px" }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submitReset}
            disabled={!canSubmitReset}
            startIcon={resetting ? <CircularProgress size={14} color="inherit" /> : <LockResetIcon />}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px", bgcolor: "#0C5A46", boxShadow: "none", "&:hover": { bgcolor: "#0a4939", boxShadow: "none" } }}
          >
            {resetting ? "Resetting…" : "Reset Password"}
          </Button>
        </DialogActions>
      </Dialog>
    </form>
  );
};

export default ParentForm;
